import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, LinearProgress, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LayersIcon from '@mui/icons-material/Layers';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import StatTile from '../../../../common/stat_tile';
import useNotification from '../../../../../core/hooks/use_notification';
import { getProductStockAction, getProductStockState } from '../../../../../features/inventory/get_product_stock';
import { getProductStockNotificationData } from '../../../../../core/utils/notification_utils';
import { GET_ROUTE_WAREHOUSE_STOCK } from '../../../../../core/constants';
import { ExpirationChip, formatCurrency, formatNumber, getStockLocationName } from '../../../warehouse/stocks/stock_utils';


const ProductBatchesStock = ({ productId }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const getProductStock = useSelector(getProductStockState);

    useEffect(() => {
        dispatch(getProductStockAction(productId))
            .then(response => showErrorMessage(getProductStockNotificationData(response)));
    }, [dispatch, productId, showErrorMessage]);

    const stock = getProductStock?.data;
    const batches = stock?.batches || [];
    const warehouseCount = new Set(batches.flatMap(batch => batch.locations.map(location => location.warehouseId))).size;
    const expiredQuantity = batches.filter(batch => batch.expired).reduce((sum, batch) => sum + batch.quantity, 0);

    if (getProductStock?.loading) {
        return <LinearProgress/>;
    }

    if (!stock) {
        return null;
    }

    return (
        <Stack spacing={ 3 }>
            <Box>
                <Grid container spacing={ 2 }>
                    <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                        <StatTile label="Sztuk na stanie" value={ formatNumber(stock.quantity) } icon={ <Inventory2Icon/> } color="primary"/>
                    </Grid>
                    <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                        <StatTile label="Partie" value={ formatNumber(batches.length) } icon={ <LayersIcon/> } color="secondary"/>
                    </Grid>
                    <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                        <StatTile label="Magazyny" value={ formatNumber(warehouseCount) } icon={ <WarehouseIcon/> } color="info"/>
                    </Grid>
                    <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                        <StatTile label="Sztuk przeterminowanych" value={ formatNumber(expiredQuantity) } icon={ <EventBusyIcon/> } color="error" valueColor={ expiredQuantity > 0 ? 'error.main' : undefined }/>
                    </Grid>
                </Grid>
            </Box>

            <TableContainer component={ Paper } variant="outlined">
                <Table>
                    <TableHead>
                        <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                            <TableCell>Partia</TableCell>
                            <TableCell align="center">Termin ważności</TableCell>
                            <TableCell align="right">Cena netto zakupu</TableCell>
                            <TableCell align="right">Sztuki</TableCell>
                            <TableCell>Gdzie się znajduje</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            batches.map(batch => (
                                <TableRow key={ `${ batch?.productBatchId }-${ batch?.warehouseBatchNumber }-${ batch?.expirationDate }` } sx={ { verticalAlign: 'top' } }>
                                    <TableCell>
                                        <Typography variant="body2" sx={ { fontWeight: 600 } }>{ batch?.warehouseBatchNumber }</Typography>
                                        <Typography variant="caption" color="text.secondary">{ batch?.productBatchId || 'brak powiązania partii' }</Typography>
                                    </TableCell>
                                    <TableCell align="center"><ExpirationChip date={ batch?.expirationDate } expired={ batch?.expired }/></TableCell>
                                    <TableCell align="right">{ formatCurrency(batch?.purchaseNetPrice) }</TableCell>
                                    <TableCell align="right" sx={ { fontWeight: 600 } }>{ formatNumber(batch?.quantity) }</TableCell>
                                    <TableCell>
                                        <Stack spacing={ 0.5 }>
                                            {
                                                batch?.locations?.map(location => (
                                                    <Stack key={ location?.warehouseId } direction="row" justifyContent="space-between" gap={ 2 }>
                                                        {
                                                            location?.warehouse
                                                                ? <Link component={ RouterLink } to={ GET_ROUTE_WAREHOUSE_STOCK(location.warehouseId) } variant="body2" underline="hover">
                                                                    { getStockLocationName(location.warehouse, location.warehouseId) }
                                                                </Link>
                                                                : <Typography variant="body2" color="text.secondary">{ getStockLocationName(null, location?.warehouseId) }</Typography>
                                                        }
                                                        <Typography variant="body2" sx={ { whiteSpace: 'nowrap' } }>{ formatNumber(location?.quantity) } szt.</Typography>
                                                    </Stack>
                                                ))
                                            }
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                        {
                            batches.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={ 5 } align="center" sx={ { py: 4, color: 'text.secondary' } }>
                                        Produkt nie znajduje się obecnie w żadnym magazynie
                                    </TableCell>
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};

export default ProductBatchesStock;
